import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { MasterAccountingTypeListResponse } from '@/entities/masters/api/generated/model';
import { connectCodeAndName } from '@/shared/lib/format/value-formatter';
import { Button, Form, Grid, InputField, SelectField } from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import {
  createExpenditureItemsSchema,
  EXPENDITURE_ITEM_LEVELS,
  type ExpenditureItemLevel,
  type ExpenditureItemsFormValues,
} from './schema';
import { useExpenditureItemsCreateMutation } from './useExpenditureItemsCreateMutation';
import { useExpenditureItemsEditMutation } from './useExpenditureItemsEditMutation';

type ExpenditureItemsFormProps = {
  onClose: () => void;
} & (
  | { mode: 'create'; accountingTypes: MasterAccountingTypeListResponse }
  | { mode: 'edit'; item: { id: string; code: string; name: string; level: ExpenditureItemLevel } }
);

export const ExpenditureItemsForm: FC<ExpenditureItemsFormProps> = (props) => {
  const { mode, onClose } = props;
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  const form = useForm<ExpenditureItemsFormValues>({
    resolver: zodResolver(createExpenditureItemsSchema(mode)),
    defaultValues: {
      level: isEdit ? props.item.level : 'kan',
      accountingTypeId: '',
      kanId: '',
      koId: '',
      code: isEdit ? props.item.code : '',
      name: isEdit ? props.item.name : '',
    },
  });

  const selectedLevel = form.watch('level');
  const selectedAccountingTypeId = form.watch('accountingTypeId');
  const selectedKanId = form.watch('kanId');

  const createMutation = useExpenditureItemsCreateMutation({ onSuccess: onClose });
  const editMutation = useExpenditureItemsEditMutation({ onSuccess: onClose });
  const isPending = isEdit ? editMutation.isPending : createMutation.isPending;

  const handleLevelChange = () => {
    form.setValue('accountingTypeId', '');
    form.setValue('kanId', '');
    form.setValue('koId', '');
    form.setValue('code', '');
    form.setValue('name', '');
  };

  const handleAccountingTypeChange = () => {
    form.setValue('kanId', '');
    form.setValue('koId', '');
  };

  const handleKanChange = () => {
    form.setValue('koId', '');
  };

  const handleSubmit = (values: ExpenditureItemsFormValues) => {
    if (isEdit) {
      const data = { code: values.code, name: values.name };
      if (props.item.level === 'kan') {
        editMutation.kanMutation.mutate({ kanId: props.item.id, data });
      } else if (props.item.level === 'ko') {
        editMutation.koMutation.mutate({ koId: props.item.id, data });
      } else {
        editMutation.mokuMutation.mutate({ mokuId: props.item.id, data });
      }
      return;
    }

    if (values.level === 'kan' && values.accountingTypeId) {
      createMutation.kanMutation.mutate({
        data: {
          accounting_type_id: values.accountingTypeId,
          code: values.code,
          name: values.name,
        },
      });
    } else if (values.level === 'ko' && values.kanId) {
      createMutation.koMutation.mutate({
        data: {
          kan_id: values.kanId,
          code: values.code,
          name: values.name,
        },
      });
    } else if (values.level === 'moku' && values.koId) {
      createMutation.mokuMutation.mutate({
        data: {
          ko_id: values.koId,
          code: values.code,
          name: values.name,
        },
      });
    }
  };

  const levelLabelMap: Record<ExpenditureItemLevel, string> = {
    kan: t('master-management.expenditure-items.create.level.kan'),
    ko: t('master-management.expenditure-items.create.level.ko'),
    moku: t('master-management.expenditure-items.create.level.moku'),
  };

  const levelOptions = EXPENDITURE_ITEM_LEVELS.map((level) => ({
    label: levelLabelMap[level],
    value: level,
  }));

  const accountingTypes = !isEdit ? props.accountingTypes : undefined;
  const accountingTypeOptions = (accountingTypes?.items ?? []).map((at) => ({
    label: connectCodeAndName({ code: at.code, name: at.name }),
    value: at.id,
  }));

  const selectedAccountingType = accountingTypes?.items.find(
    (at) => at.id === selectedAccountingTypeId,
  );
  const kanOptions = (selectedAccountingType?.kans ?? []).map((kan) => ({
    label: connectCodeAndName({ code: kan.code, name: kan.name }),
    value: kan.id,
  }));

  const selectedKan = selectedAccountingType?.kans.find((kan) => kan.id === selectedKanId);
  const koOptions = (selectedKan?.kos ?? []).map((ko) => ({
    label: connectCodeAndName({ code: ko.code, name: ko.name }),
    value: ko.id,
  }));

  const needsKanSelector = !isEdit && (selectedLevel === 'ko' || selectedLevel === 'moku');
  const needsKoSelector = !isEdit && selectedLevel === 'moku';

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <Grid
            columns={1}
            items={[
              [
                {
                  key: 'level',
                  field: (
                    <SelectField
                      control={form.control}
                      name="level"
                      label={t('master-management.expenditure-items.create.field.level')}
                      options={levelOptions}
                      required
                      disabled={isEdit}
                      onValueChange={isEdit ? undefined : handleLevelChange}
                    />
                  ),
                },
                ...(!isEdit
                  ? [
                      {
                        key: 'accountingTypeId',
                        field: (
                          <SelectField
                            control={form.control}
                            name="accountingTypeId"
                            label={t('master-management.expenditure-items.table.accounting-type')}
                            options={accountingTypeOptions}
                            required
                            showSearch
                            onValueChange={handleAccountingTypeChange}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(needsKanSelector
                  ? [
                      {
                        key: 'kanId',
                        field: (
                          <SelectField
                            control={form.control}
                            name="kanId"
                            label={t('master-management.expenditure-items.create.field.kan')}
                            options={kanOptions}
                            required
                            showSearch
                            disabled={!selectedAccountingTypeId}
                            onValueChange={handleKanChange}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(needsKoSelector
                  ? [
                      {
                        key: 'koId',
                        field: (
                          <SelectField
                            control={form.control}
                            name="koId"
                            label={t('master-management.expenditure-items.create.field.ko')}
                            options={koOptions}
                            required
                            showSearch
                            disabled={!selectedKanId}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'code',
                  field: (
                    <InputField
                      control={form.control}
                      name="code"
                      label={t('master-management.common-table.code')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
                {
                  key: 'name',
                  field: (
                    <InputField
                      control={form.control}
                      name="name"
                      label={t('master-management.common-table.name')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
              ],
            ]}
          />
        </div>

        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
